import type { FunctionComponent, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { intl } from '@trello/i18n';
import { OrganizationVisibleIcon } from '@trello/nachos/icons/organization-visible';
import { PublicIcon } from '@trello/nachos/icons/public';
import type { PIIString } from '@trello/privacy';
import { token } from '@trello/theme';

import { LinkCopy } from 'app/src/components/LinkCopy';
import { SocialShareLinks } from 'app/src/components/SocialShareLinks';

import styles from './ShareTemplatePopover.less';

const TemplatePopover: FunctionComponent<{
  boardId: string;
  linkOnClick: () => void;
  teamId: string;
  url: string;
  visibility: string;
  name: string;
  visibilityText?: ReactNode;
}> = ({
  boardId,
  linkOnClick,
  teamId,
  url,
  visibility,
  name,
  visibilityText,
}) => {
  let icon;
  if (visibility === 'public') {
    icon = (
      <PublicIcon
        dangerous_className={styles.visibilityIcon}
        color={token('color.icon.accent.green', '#22A06B')}
      />
    );
  } else {
    icon = (
      <OrganizationVisibleIcon
        dangerous_className={styles.visibilityIcon}
        color={token('color.icon.accent.yellow', '#B38600')}
      />
    );
  }

  return (
    <>
      <FormattedMessage
        id="share template popover.share a view only link with your team"
        defaultMessage="Share a view-only link so others can copy this template."
        description="Text on popover for sharing a view-only link of a template"
      />
      <LinkCopy
        url={url}
        analyticsSource={'shareTemplateInlineDialog'}
        boardId={boardId}
        teamId={teamId}
      />
      <SocialShareLinks
        url={url}
        tweet={intl.formatMessage(
          {
            id: 'share template popover.share template tweet',
            defaultMessage:
              'No more starting from scratch. Check out this free template from @Trello: {templateName}',
            description: 'Tweet for sharing a template link',
          },
          {
            templateName: name,
          },
        )}
        emailBody={intl.formatMessage(
          {
            id: 'share template popover.share template email',
            defaultMessage:
              'No more starting from scratch. Check out this free template from Trello: {templateName}',
            description: 'Email for sharing a template link',
          },
          {
            templateName: name,
          },
        )}
        analyticsSource={'shareTemplateInlineDialog'}
        hashtags={['trellotemplates']}
        boardId={boardId}
      />
      {visibilityText && (
        <>
          <hr role="presentation" />
          <div>
            {icon}
            <span className={styles.visibilityText}>{visibilityText}</span>
          </div>
        </>
      )}
      <hr role="presentation" />
      <a
        href="https://help.trello.com/article/1187-creating-template-boards"
        className={styles.learnMoreLink}
        onClick={linkOnClick}
        rel="noopener noreferrer"
        target="_blank"
        title={intl.formatMessage({
          id: 'share template popover.learn more about templates',
          defaultMessage: 'Learn more about templates',
          description: 'Title for link to page to learn more about templates',
        })}
      >
        <FormattedMessage
          id="share template popover.learn more about templates"
          defaultMessage="Learn more about templates"
          description="Link to page to learn more about templates"
        />
      </a>
    </>
  );
};

const TemplateGoldPopover: FunctionComponent<{
  url: string;
  boardId: string;
  linkOnClick: () => void;
  teamId: string;
  name: string;
}> = ({ boardId, teamId, url, linkOnClick, name }) => (
  <>
    <LinkCopy
      url={url}
      analyticsSource={'shareTemplateInlineDialog'}
      boardId={boardId}
      teamId={teamId}
      isTemplate
    />
    <SocialShareLinks
      url={url}
      tweet={intl.formatMessage(
        {
          id: 'share template popover.share template tweet',
          defaultMessage:
            'No more starting from scratch. Check out this free template from @Trello: {templateName}',
          description: 'Tweet for sharing a template link',
        },
        {
          templateName: name,
        },
      )}
      emailBody={intl.formatMessage(
        {
          id: 'share template popover.share template email',
          defaultMessage:
            'No more starting from scratch. Check out this free template from Trello: {templateName}',
          description: 'Email for sharing a template link',
        },
        {
          templateName: name,
        },
      )}
      analyticsSource={'shareTemplateInlineDialog'}
      hashtags={['trellotemplates']}
      boardId={boardId}
    />
    <hr role="presentation" />
    <div>
      <PublicIcon
        dangerous_className={styles.visibilityIcon}
        color={token('color.icon.accent.green', '#22A06B')}
      />
      <span className={styles.visibilityText}>
        <FormattedMessage
          id="share template popover.anyone on the internet can see this template"
          defaultMessage="Anyone on the internet can see this template."
          description="Text describing visibility of a public template"
        />
      </span>
    </div>
  </>
);

export const ShareTemplatePopover: FunctionComponent<{
  name?: string;
  orgName?: string;
  enterpriseName?: string;
  url?: string;
  storyPageUrl?: string;
  visibility?: string;
  boardId?: string;
  teamId?: string;
  username?: PIIString;
}> = ({
  name = '',
  orgName,
  enterpriseName,
  url = '',
  storyPageUrl,
  visibility = '',
  boardId = '',
  teamId = '',
  username,
}) => {
  if (visibility === 'public' && username) {
    return (
      <div className={styles.popoverContent}>
        <TemplateGoldPopover
          name={name}
          url={storyPageUrl || url}
          boardId={boardId}
          teamId={teamId}
          linkOnClick={() => {
            Analytics.sendClickedLinkEvent({
              linkName: 'goldLink',
              source: 'shareTemplateInlineDialog',
              containers: {
                board: {
                  id: boardId,
                },
              },
            });
          }}
        />
      </div>
    );
  }

  let visibilityText;
  // not handling private visiblity because we don't show popover on private templates
  if (enterpriseName && visibility === 'enterprise') {
    visibilityText = (
      <FormattedMessage
        id="share template popover.all members of org can see this template"
        defaultMessage="All members of {orgName} can see this template."
        description="Text describing visibility of an enterprise-visible template"
        values={{ orgName: enterpriseName }}
      />
    );
  } else if (orgName && visibility === 'org') {
    visibilityText = (
      <FormattedMessage
        id="share template popover.all members of org can see this template"
        defaultMessage="All members of {orgName} can see this template."
        description="Text describing visibility of an organization-visible template"
        values={{ orgName }}
      />
    );
  } else if (visibility === 'public') {
    visibilityText = (
      <FormattedMessage
        id="share template popover.anyone on the internet can see this template"
        defaultMessage="Anyone on the internet can see this template."
        description="Text describing visibility of a public template"
      />
    );
  }

  return (
    <div className={styles.popoverContent}>
      <TemplatePopover
        url={storyPageUrl || url}
        boardId={boardId}
        teamId={teamId}
        visibility={visibility}
        visibilityText={visibilityText}
        name={name}
        linkOnClick={() => {
          Analytics.sendClickedLinkEvent({
            linkName: 'templatesHelpLink',
            source: 'shareTemplateInlineDialog',
            containers: {
              board: {
                id: boardId,
              },
            },
          });
        }}
      />
    </div>
  );
};
